import React from "react";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import QuizIcon from "@mui/icons-material/Quiz";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import SmartphoneOutlinedIcon from "@mui/icons-material/SmartphoneOutlined";
import QuizEditorModal from "./QuizEditorModal";
import CompleteQuizModal from "./CompleteQuizModal";
import { formatDate } from "../requests/utilities/helpers";
import { getRightworkAPI } from "../requests/utilities/requests";
import { API_get_learning_quiz_detail } from "../../../constants";
import { TrainingEmptyState } from "./TrainingEmptyState";
import LibraryFilterChips from "./LibraryFilterChips";

const ASSIGNABLE_OPTIONS = [
  { value: "assignable", label: "Assignable" },
  { value: "not_assignable", label: "Not assignable" },
];

const filterSelectSx = {
  height: 42,
  minWidth: 170,
  borderRadius: "8px",
  backgroundColor: "white",
  fontSize: 14,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#a78bfa" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
  "& .MuiSelect-select": {
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "12px",
  },
};

function QuizzesDisplay(props) {
  const [open, setOpen] = React.useState(false);
  const [modalEdit, setModalEdit] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  // Set when an edit-quiz detail fetch fails; blocks the editor body + Save so
  // a failed load can't leave the previous quiz on screen and overwrite it.
  const [quizLoadError, setQuizLoadError] = React.useState(false);
  const [openQuizPreviewModal, setOpenQuizPreviewModal] = React.useState(false);
  const [previewViewport, setPreviewViewport] = React.useState("desktop");
  const [previewMenuAnchor, setPreviewMenuAnchor] = React.useState(null);
  const [pendingPreviewUuid, setPendingPreviewUuid] = React.useState(null);
  const [searchInput, setSearchInput] = React.useState("");
  const [categoryFilter, setCategoryFilter] = React.useState("all");
  const [assignableFilter, setAssignableFilter] = React.useState("all");
  const [sortKey, setSortKey] = React.useState("recent");

  const quizzes = React.useMemo(
    () => props.quizzesData || [],
    [props.quizzesData]
  );

  const categories = React.useMemo(() => {
    return [...new Set(quizzes.map((quiz) => quiz.category).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));
  }, [quizzes]);

  const filteredQuizzes = React.useMemo(() => {
    const searchParts = searchInput.toLowerCase().split(" ").filter(Boolean);
    const filtered = quizzes.filter((quiz) => {
      const haystack = [
        quiz.name,
        quiz.category,
        quiz.pass_percentage ? `${quiz.pass_percentage}%` : null,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (!searchParts.every((part) => haystack.includes(part))) return false;
      if (categoryFilter !== "all" && (quiz.category || "") !== categoryFilter) {
        return false;
      }
      if (assignableFilter === "assignable" && !quiz.is_directly_assignable) {
        return false;
      }
      if (assignableFilter === "not_assignable" && quiz.is_directly_assignable) {
        return false;
      }
      return true;
    });

    return [...filtered].sort((a, b) => {
      if (sortKey === "name") {
        return (a.name || "").localeCompare(b.name || "");
      }
      if (sortKey === "questions") {
        return (b.learning_questions_count || 0) - (a.learning_questions_count || 0);
      }
      if (sortKey === "passing") {
        return (b.pass_percentage || 0) - (a.pass_percentage || 0);
      }
      return (b.updated_at || b.created_at || 0) - (a.updated_at || a.created_at || 0);
    });
  }, [quizzes, searchInput, categoryFilter, assignableFilter, sortKey]);

  const activeFilterCount =
    (categoryFilter !== "all" ? 1 : 0) +
    (assignableFilter !== "all" ? 1 : 0);
  const hasSearchOrFilters = Boolean(searchInput || activeFilterCount);

  const fetchQuizDetail = async (showLoading, learning_quiz_uuid) => {
    setIsLoading(showLoading);
    setQuizLoadError(false);
    let api_params = {
      company_id: props.homeCompany,
      learning_quiz_uuid: learning_quiz_uuid,
      // Surface the reuse signal (used-in-N-sections) in the editor header.
      include_section_usage: true,
    };
    try {
      const res = await getRightworkAPI(
        API_get_learning_quiz_detail,
        api_params
      );
      if (res.status === 200) {
        props.setModalDataQuiz(res.data.redux?.payload?.learning_quiz);
        setIsLoading(false);
      } else {
        // Sentinel failure (getRightworkAPI returns {status:0} on some errors):
        // flag it so the editor blocks Save instead of opening against the
        // previously-loaded quiz.
        setQuizLoadError(true);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Promise failed", error);
      setQuizLoadError(true);
      setIsLoading(false);
    }
  };

  const createQuiz = () => {
    setModalEdit(false);
    setQuizLoadError(false);
    props.setModalDataQuiz(props.default_data_quiz);
    setOpen(true);
  };

  const editQuiz = (quiz) => {
    setModalEdit(true);
    fetchQuizDetail(true, quiz.uuid);
    setOpen(true);
  };

  const handleOpenPreviewMenu = (event, quizUuid) => {
    event.stopPropagation();
    setPendingPreviewUuid(quizUuid);
    setPreviewMenuAnchor(event.currentTarget);
  };

  const handlePreviewIn = (mode) => {
    if (pendingPreviewUuid) {
      fetchQuizDetail(true, pendingPreviewUuid);
    }
    setPreviewViewport(mode);
    setPreviewMenuAnchor(null);
    setOpenQuizPreviewModal(true);
  };

  const clearSearchAndFilters = () => {
    setSearchInput("");
    setCategoryFilter("all");
    setAssignableFilter("all");
  };

  return (
    <>
      <div className="min-h-screen bg-white">
        <div className="bg-white border-b border-gray-200">
          <div className="px-6 py-8">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h1 className="mb-1 text-2xl font-bold text-slate-900">
                  Learning Quizzes
                </h1>
                <p className="text-sm text-slate-600">
                  Assessment tools to test knowledge and understanding
                </p>
              </div>
              <Tooltip title="Create new quiz">
                <button
                  className="flex items-center gap-2 rounded-lg bg-violet-700 px-6 py-3 text-white shadow-sm transition-colors hover:bg-violet-800"
                  onClick={createQuiz}
                >
                  <AddIcon style={{ fontSize: "20px" }} />
                  <span className="font-medium">New Quiz</span>
                </button>
              </Tooltip>
            </div>

            <div className="mb-6 flex items-center gap-6 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <QuizIcon style={{ fontSize: "18px" }} />
                <span>
                  {filteredQuizzes.length}{" "}
                  {filteredQuizzes.length === 1 ? "Quiz" : "Quizzes"}
                </span>
              </div>
              {searchInput && (
                <div className="flex items-center gap-2 text-violet-600">
                  <SearchIcon style={{ fontSize: "16px" }} />
                  <span>Showing search results</span>
                </div>
              )}
              {activeFilterCount > 0 && (
                <div className="flex items-center gap-2 text-violet-600">
                  <FilterListIcon style={{ fontSize: "16px" }} />
                  <span>
                  {activeFilterCount}{" "}
                  {activeFilterCount === 1 ? "filter" : "filters"} active
                </span>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="relative w-full max-w-md">
                <SearchIcon
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  style={{ fontSize: "20px" }}
                />
                <input
                  className="w-full rounded-lg border border-slate-200 py-2.5 pl-10 pr-4 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-100"
                  type="text"
                  placeholder="Search quizzes..."
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                />
              </div>

              <Select
                size="small"
                displayEmpty
                sx={filterSelectSx}
                value={categoryFilter}
                onChange={(event) => setCategoryFilter(event.target.value)}
              >
                <MenuItem value="all" sx={{ fontSize: 14 }}>
                  All categories
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category} value={category} sx={{ fontSize: 14 }}>
                    {category}
                  </MenuItem>
                ))}
              </Select>

              <Select
                size="small"
                sx={filterSelectSx}
                value={sortKey}
                onChange={(event) => setSortKey(event.target.value)}
              >
                <MenuItem value="recent" sx={{ fontSize: 14 }}>
                  Recently updated
                </MenuItem>
                <MenuItem value="name" sx={{ fontSize: 14 }}>
                  Name
                </MenuItem>
                <MenuItem value="questions" sx={{ fontSize: 14 }}>
                  Most questions
                </MenuItem>
                <MenuItem value="passing" sx={{ fontSize: 14 }}>
                  Highest passing %
                </MenuItem>
              </Select>
            </div>

            <div className="mt-4">
              <LibraryFilterChips
                groups={[
                  {
                    key: "assignable",
                    value: assignableFilter,
                    onChange: setAssignableFilter,
                    options: ASSIGNABLE_OPTIONS,
                  },
                ]}
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-8">
          {filteredQuizzes.length === 0 ? (
            <TrainingEmptyState
              icon={QuizIcon}
              plural="quizzes"
              blurb="Create checks that test knowledge and can be added to training sections."
              createLabel="Create your first quiz"
              searching={hasSearchOrFilters}
              noResultsTitle="No quizzes match your filters"
              noResultsBlurb="Try adjusting your search or clearing the selected filters."
              clearLabel="Clear filters"
              onClear={hasSearchOrFilters ? clearSearchAndFilters : undefined}
              onCreate={createQuiz}
            />
          ) : (
            <div className="grid grid-cols-2 gap-3 xl:grid-cols-1">
              {filteredQuizzes.map((quiz) => (
                <QuizCard
                  key={quiz.uuid}
                  quiz={quiz}
                  onEdit={() => editQuiz(quiz)}
                  onPreview={(event) => handleOpenPreviewMenu(event, quiz.uuid)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <QuizEditorModal
        open={open}
        handleClose={() => setOpen(false)}
        modalEdit={modalEdit}
        isLoading={isLoading}
        loadError={quizLoadError}
        modalDataQuiz={props.modalDataQuiz}
        setModalDataQuiz={props.setModalDataQuiz}
        post_learning_quiz={props.post_learning_quiz}
        delete_learning_quiz={props.delete_learning_quiz}
        homeCompany={props.homeCompany}
        quizzesData={props.quizzesData}
        sectionsData={props.sectionsData}
        modulesData={props.modulesData}
      />
      <Menu
        anchorEl={previewMenuAnchor}
        open={Boolean(previewMenuAnchor)}
        onClose={() => setPreviewMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => handlePreviewIn("desktop")}>
          <ListItemIcon>
            <DesktopWindowsOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Desktop"
            secondary="Browser-sized preview"
          />
        </MenuItem>
        <MenuItem onClick={() => handlePreviewIn("mobile")}>
          <ListItemIcon>
            <SmartphoneOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Mobile"
            secondary="Phone-sized preview"
          />
        </MenuItem>
      </Menu>

      <CompleteQuizModal
        open={openQuizPreviewModal}
        handleClose={() => setOpenQuizPreviewModal(false)}
        isLoading={isLoading}
        modalDataQuiz={props.modalDataQuiz}
        type="Preview"
        mobilePreview={previewViewport === "mobile"}
      />
    </>
  );
}

function QuizCard({ quiz, onEdit, onPreview }) {
  const questionCount = quiz.learning_questions_count || 0;
  const questionLabel = `${questionCount} ${
    questionCount === 1 ? "question" : "questions"
  }`;
  const passingLabel = quiz.pass_percentage
    ? `${quiz.pass_percentage}% passing`
    : "No passing score";
  const attemptsLabel = quiz.retake_limit
    ? `${quiz.retake_limit} ${quiz.retake_limit === 1 ? "attempt" : "attempts"}`
    : "Unlimited attempts";
  const updatedLabel = quiz.updated_at
    ? `Updated ${formatDate(quiz.updated_at)}`
    : null;

  return (
    <div
      className="group cursor-pointer rounded-lg border border-slate-200 bg-white p-4 transition-all duration-200 hover:border-slate-300 hover:shadow-md"
      onClick={onEdit}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-700">
          <QuizIcon className="text-white" style={{ fontSize: 20 }} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="line-clamp-2 text-sm font-semibold text-slate-900">
                {quiz.name || "Untitled quiz"}
              </h3>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                <span>{questionLabel}</span>
                <span>·</span>
                <span>{passingLabel}</span>
                <span>·</span>
                <span>{attemptsLabel}</span>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
              <Tooltip title="Preview quiz">
                <IconButton size="small" onClick={onPreview}>
                  <VisibilityIcon style={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit quiz">
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    onEdit();
                  }}
                >
                  <EditIcon style={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              {quiz.category && (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                  {quiz.category}
                </span>
              )}
              {quiz.is_directly_assignable && (
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-100">
                  Assignable
                </span>
              )}
              {quiz.show_correct_answers && (
                <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-blue-100">
                  Shows answers
                </span>
              )}
            </div>
            {updatedLabel && (
              <span className="shrink-0 text-xs text-slate-400">
                {updatedLabel}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default QuizzesDisplay;
